import React, { useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Divider,
  alpha,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { 
  Check as CheckIcon,
  ExpandMore,
  Psychology,
  AccessTime,
  School,
  WorkOutline,
  CommentBank,
  Person,
  EmojiEvents,
  MenuBook,
  Star,
  Assessment,
  PersonSearch,
  LinkedIn,
  AssignmentTurnedIn,
  VpnKey,
  Description,
  Email,
  ListAlt,
  HourglassEmpty,
  EventNote,
  Assignment,
  WhatsApp
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import { useTheme } from '@mui/material/styles';
import PageHeader from '../../components/PageHeader';

const IndividueleCoaching = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  const theme = useTheme();
  const content = t.individueleCoaching;

  return (
    <>
      <PageHeader 
        title={t.diensten.title}
        subtitle={content.title}
      />

      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 8 } }}>
        <Box sx={{ mb: 6 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="body1" fontWeight="medium">
                {language === 'nl' ? 'Wat is individuele coaching en hoe werkt het?' : 'What is individual coaching and how does it work?'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" paragraph>
                {content.description}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            {language === 'nl' ? 'Wat kun je verwachten?' : 'What can you expect?'}
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Psychology color="primary" sx={{ fontSize: 32, mr: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    {language === 'nl' ? 'Persoonlijke begeleiding' : 'Personal guidance'}
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {language === 'nl' 
                    ? 'Een coach die luistert voorbij jouw woorden en hoort wat je écht nodig hebt'
                    : 'A coach who listens beyond your words and hears what you really need'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Assessment color="primary" sx={{ fontSize: 32, mr: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    {language === 'nl' ? 'Duidelijk inzicht' : 'Clear insight'}
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {language === 'nl'
                    ? 'Duidelijk inzicht in je waarden, talenten en carrière doelen'
                    : 'Clear insight into your values, talents and career goals'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WorkOutline color="primary" sx={{ fontSize: 32, mr: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    {language === 'nl' ? 'Op maat gemaakt stappenplan' : 'Tailored roadmap'}
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {language === 'nl'
                    ? 'Een persoonlijk, op maat gemaakt stappenplan om je doelen snel te realiseren'
                    : 'A personalized, tailored roadmap to achieve your goals quickly'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CommentBank color="primary" sx={{ fontSize: 32, mr: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    {language === 'nl' ? 'Veilige omgeving' : 'Safe environment'}
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {language === 'nl'
                    ? 'Een veilige omgeving, waarin je eerlijk kunt zijn over jezelf en je ambities'
                    : 'A safe environment where you can be honest about yourself and your ambitions'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h2" gutterBottom>
            {language === 'nl' ? 'Wat kost loopbaanbegeleiding?' : 'What are the coaching costs?'}
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: '800px', mx: 'auto', mb: 2 }}>
            {language === 'nl' 
              ? 'Selecteer het pakket dat het beste bij jouw behoeften past en start vandaag nog met je persoonlijke ontwikkeling.'
              : 'Select the package that best suits your needs and start your personal development today.'}
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ mb: 8 }} justifyContent="center">
          <Grid item xs={12} sm={8} md={6} lg={5}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              boxShadow: 3,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 6
              }
            }}>
              <Box sx={{ 
                bgcolor: theme.palette.primary.light,
                color: 'white',
                p: 3, 
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 110
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                   <HourglassEmpty color="inherit" sx={{ mr: 1, fontSize: 28 }} />
                   <Typography variant="h5" component="h3" fontWeight={600} color="inherit">
                     {language === 'nl' ? 'Losse Sessie' : 'Single Session'}
                   </Typography>
                </Box>
                 <Typography variant="subtitle1" color="inherit" sx={{ opacity: 0.9, fontSize: '0.95rem' }}>
                   {language === 'nl' ? 'Flexibele Ondersteuning' : 'Flexible Support'}
                 </Typography>
              </Box>
              
              <CardContent sx={{ flexGrow: 1, p: 2, pt: 3, display: 'flex', flexDirection: 'column' }}>
                <List dense sx={{ mb: 2 }}>
                   {[
                     { text: content.pricing.singleSessions.items[0], icon: <AccessTime color="primary" /> },
                     { text: content.pricing.singleSessions.items[1], icon: <EventNote color="primary" /> },
                     { text: content.pricing.singleSessions.items[2], icon: <MenuBook color="primary" /> },
                     { text: content.pricing.singleSessions.items[3], icon: <Assignment color="primary" /> },
                     { text: content.pricing.singleSessions.items[4], icon: <Email color="primary" /> },
                   ].map((item, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                    </ListItem>
                  ))}
                </List>
                
                <Box sx={{ flexGrow: 1 }} />

                <Box sx={{ mt: 'auto', borderTop: `1px solid ${theme.palette.divider}`, pt: 1 }}>
                  <Accordion elevation={0} disableGutters sx={{ bgcolor: 'transparent', '&::before': { display: 'none' } }}>
                    <AccordionSummary 
                      expandIcon={<ExpandMore fontSize="small" />}
                      aria-controls="single-session-content"
                      id="single-session-header"
                       sx={{ minHeight: '36px', py: 0, px: 0, '& .MuiAccordionSummary-content': { m: 0 } }}
                    >
                       <Box sx={{ display: 'flex', alignItems: 'center' }}>
                         <Person fontSize="small" sx={{ mr: 1, color: theme.palette.primary.main }} />
                         <Typography variant="body2" fontWeight="medium" fontSize="0.9rem">
                           {language === 'nl' ? 'Voor wie?' : 'Who is this for?'}
                         </Typography>
                       </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
                       <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                        {language === 'nl' 
                          ? 'Ideaal voor een eerste kennismaking, een CV-check, of het voorbereiden van een specifiek sollicitatiegesprek.'
                          : 'Ideal for a first meeting, a CV check, or preparing for a specific job interview.'}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                 </Box>
              </CardContent>
               <CardActions sx={{ p: 2, pt: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                 <Box sx={{ width: '100%', mb: 1, textAlign: 'center', mt: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {content.pricing.singleSessions.price}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                     {language === 'nl' ? '(incl. BTW)' : '(incl. VAT)'}
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                   size="large"
                   sx={{ bgcolor: theme.palette.primary.light, '&:hover': { bgcolor: theme.palette.primary.main } }}
                   onClick={() => navigate('/contact', { state: { subject: language === 'nl' ? 'Afspraak Losse Coachsessie' : 'Appointment Single Coaching Session' } })}
                >
                   {language === 'nl' ? 'Maak een afspraak' : 'Make an appointment'}
                 </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} sm={8} md={6} lg={5}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              boxShadow: 4,
              border: `2px solid #7D93A4`,
              position: 'relative',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 6
              }
            }}>
              <Box sx={{ 
                background: 'linear-gradient(145deg, #7D93A4 0%, #A5B6C5 100%)',
                color: 'white',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 110
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                   <Psychology color="inherit" sx={{ mr: 1, fontSize: 28 }} />
                   <Typography variant="h5" component="h3" fontWeight={600}>
                     {language === 'nl' ? content.pricing.package.title : 'Platinum Program'}
                   </Typography>
                 </Box>
                 <Typography variant="subtitle1" color="white" sx={{ opacity: 0.9, fontSize: '0.95rem' }}>
                    {language === 'nl' ? 'Bereik je volledige potentieel' : 'Reach your full potential'}
                 </Typography>
               </Box>
               
               <CardContent sx={{ flexGrow: 1, p: 2, pt: 3, display: 'flex', flexDirection: 'column' }}>
                 <List dense sx={{ mb: 2 }}>
                    {[
                      { text: content.pricing.package.items[0], icon: <MenuBook sx={{ color: '#7D93A4' }} /> },
                      { text: content.pricing.package.items[1], icon: <CommentBank sx={{ color: '#7D93A4' }} /> },
                      { text: content.pricing.package.items[2], icon: <Psychology sx={{ color: '#7D93A4' }} /> },
                      { text: content.pricing.package.items[3], icon: <ListAlt sx={{ color: '#7D93A4' }} /> },
                      { text: content.pricing.package.items[4], icon: <WhatsApp sx={{ color: '#7D93A4' }} /> },
                    ].map((item, index) => (
                     <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                       <ListItemIcon sx={{ minWidth: 36 }}>
                         {item.icon}
                       </ListItemIcon>
                       <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                     </ListItem>
                   ))}
                 </List>

                 <Box sx={{ flexGrow: 1 }} /> 

                 <Box sx={{ mt: 'auto', borderTop: `1px solid ${theme.palette.divider}`, pt: 1 }}>
                   <Accordion elevation={0} disableGutters sx={{ bgcolor: 'transparent', '&::before': { display: 'none' } }}>
                     <AccordionSummary 
                       expandIcon={<ExpandMore fontSize="small" />}
                       aria-controls="platinum-who-content"
                       id="platinum-who-header"
                       sx={{ minHeight: '36px', py: 0, px: 0, '& .MuiAccordionSummary-content': { m: 0 } }}
                     >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                         <Person fontSize="small" sx={{ mr: 1, color: '#7D93A4' }} />
                         <Typography variant="body2" fontWeight="medium" fontSize="0.9rem">
                            {language === 'nl' ? 'Voor wie?' : 'Who is this for?'}
                         </Typography>
                       </Box>
                     </AccordionSummary>
                     <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
                       <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                         {language === 'nl' 
                           ? 'Je zoekt gerichte begeleiding bij het bereiken van je doelen en wilt een coach die met je meedenkt. Je bent bereid te investeren in jezelf voor blijvende persoonlijke ontwikkeling.'
                           : 'For professionals who want to seriously work on their development and need in-depth, structural guidance.'}
                       </Typography>
                     </AccordionDetails>
                   </Accordion>
                 </Box>
               </CardContent>
               <CardActions sx={{ p: 2, pt: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                 <Box sx={{ width: '100%', mb: 1, textAlign: 'center', mt: 1 }}>
                   <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                     {content.pricing.package.price}
                   </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                     {language === 'nl' ? '(incl. BTW)' : '(incl. VAT)'}
                   </Typography>
                 </Box>
                 <Button
                   fullWidth
                   variant="contained"
                   size="large"
                   sx={{ bgcolor: '#7D93A4', '&:hover': { bgcolor: '#6B8191' } }}
                   onClick={() => navigate('/contact', { state: { subject: language === 'nl' ? content.pricing.package.title : 'Platinum Program' } })}
                 >
                    {language === 'nl' ? content.pricing.package.button : 'Start your program'}
                 </Button>
               </CardActions>
             </Card>
           </Grid>
         </Grid>

        <Box sx={{ 
          p: { xs: 3, md: 4 }, 
          bgcolor: alpha(theme.palette.primary.main || '#BEB4A7', 0.08),
          mb: 6,
          borderRadius: 2,
          textAlign: 'center'
        }}>
          <Typography variant="h5" gutterBottom sx={{ 
            color: 'primary.main', 
            mb: 3, 
          }}>
            {content.cta.title}
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ 
            mb: 3,
            fontSize: '1.1rem',
            lineHeight: 1.7,
            color: 'text.secondary',
            maxWidth: '700px',
            mx: 'auto'
          }}>
            {content.cta.description}
          </Typography>
          
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'center'
          }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={() => navigate('/contact', { state: { subject: content.title } })}
              sx={{ px: 4, py: 1.5 }}
            >
              {content.cta.button || t.diensten.cta.scheduleButton}
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default IndividueleCoaching; 